// Conversations — domain types. These mirror conversation-service's JSON exactly
// (see docs/conversations/00-…spec.md §8 and the service README). Keep field names
// snake_case to match the wire; the UI maps to view models where it wants camelCase.

export type ConversationType = 'DIRECT' | 'GROUP' | 'TEAM' | 'DEPARTMENT' | 'COMPANY' | 'CONTEXTUAL';
export type PostingPolicy = 'ALL_MEMBERS' | 'ADMINS_ONLY' | 'OWNERS_ONLY';
export type ConversationStatus = 'ACTIVE' | 'ARCHIVED' | 'CLOSED';
export type MessageKind = 'TEXT' | 'SYSTEM' | 'MEETING_CARD' | 'ACTION_CARD' | 'FILE';

export interface Conversation {
  id: string;
  type: ConversationType;
  title?: string;
  context_type?: string;
  context_id?: string;
  posting_policy: PostingPolicy;
  created_by: string;
  last_message_seq: number;
  last_message_at?: string;
  status: ConversationStatus;
  created_at: string;
  // Details/Edit
  description?: string;
  avatar_file_ref?: string;
  updated_at?: string;
}

export type MemberRole = 'OWNER' | 'ADMIN' | 'MEMBER' | 'BOT';
export type NotificationLevel = 'ALL' | 'MENTIONS' | 'NONE';

/** Details → Members row (roles + join date + the member's notification level). */
export interface ConversationMember {
  user_id: string;
  employee_id?: string;
  role: MemberRole;
  membership_source: string;
  joined_at: string;
  notification_level: NotificationLevel | 'INHERIT';
  muted_until?: string;
  name?: string;
}

/** Inbox row: a conversation plus the caller's unread hint and participant set. */
export interface ConversationSummary extends Conversation {
  unread_hint: number;
  is_favorite?: boolean;
  participant_user_ids: string[];
  /** The caller's own role in this conversation. Absent ⇒ treat as MEMBER. */
  my_role?: MemberRole;
  /** Server-resolved row action ("get this off my list"). The clients render it;
   *  see rowActions.ts for the rule and the fallback when it's absent. */
  list_action?: 'REMOVE' | 'LEAVE' | 'ARCHIVE';
}

export interface Message {
  id: string;
  conversation_id: string;
  seq: number;
  client_message_id?: string;
  sender_user_id: string;
  kind: MessageKind;
  body_plain: string;
  reply_to_message_id?: string;
  thread_root_message_id?: string;
  edited_at?: string;
  deleted_at?: string;
  created_at: string;
  // Read-side enrichment.
  mentioned_user_ids?: string[];
  reactions?: Record<string, number>; // reaction_key -> count
  my_reactions?: string[];
  /** Rich object card (assignment, leave, expense, meeting, …) for
   *  MEETING_CARD/ACTION_CARD/SYSTEM messages — lifted server-side from metadata. */
  card?: MessageCard;
  /** Dana confirm-first proposals (§12.3) on a bot message — the thread renders a
   *  confirm card; confirming calls ai-agent /api/v1/ai/actions/{id}/confirm. */
  proposals?: DanaProposal[];
  /** Whose proposals these are. The action is actor-bound: only this person can
   *  confirm, and everybody else in the thread is shown who it waits on. */
  proposal_owner_user_id?: string;
  /** A provider-hosted GIF rendered inline (references only, we store no bytes). */
  gif?: MessageGif;
  /** A poll (question + options), enriched with counts + the caller's votes on read. */
  poll?: MessagePoll;
  /** An uploaded file attachment (download via the gated file endpoint). */
  file?: MessageFile;
  /** CLIENT-ONLY send state. The server never sends this.
   *
   *  A failed send used to be expressed by rewriting the message into a SYSTEM
   *  kind whose body started with "⚠", and the UI sniffed that prefix. That
   *  destroyed the original kind, poll and file, hard-coded English into a
   *  value, and would misread a genuine SYSTEM message that happened to start
   *  with a warning sign. The message now keeps its real content and carries
   *  its state beside it. */
  send_state?: 'sending' | 'failed';
}

export interface MessageFile {
  key: string;
  name: string;
  mime?: string;
  size?: number;
  saved_to_documents?: boolean;
}

export interface MessageGif {
  url: string;
  preview?: string;
  title?: string;
  width?: number;
  height?: number;
}

export interface PollOption {
  id: string;
  text: string;
}

export interface MessagePoll {
  question: string;
  options: PollOption[];
  multi?: boolean;
  counts?: Record<string, number>;
  my_votes?: string[];
}

/** The client's poll-create input (server assigns option ids if omitted). */
export interface NewPoll {
  question: string;
  options: { text: string }[];
  multi?: boolean;
}

/** A Dana confirm-first action proposal (the ai-agent PendingAgentAction shape). */
export interface DanaProposal {
  action_id: string;
  tool: string;
  summary: string;
  payload?: Record<string, unknown>;
  payload_sha256: string;
  status: string;
  expires_at?: string;
}

/** A rich object card rendered inline in a message. Free-form fields; the producer
 *  (a domain service or Dana) fills what's relevant to the object. */
export interface MessageCard {
  type?: string; // WORK_ITEM | ASSIGNMENT | LEAVE | EXPENSE | MEETING | …
  entity_id?: string;
  title?: string;
  subtitle?: string;
  status?: string;
  fields?: Array<{ label: string; value: string }>;
  action_url?: string;
  // MEETING card specifics (present when type === 'MEETING'):
  join_url?: string;
  provider?: string;
  start_at?: string;
  end_at?: string;
  attendee_count?: number;
}

/** One discoverable meeting provider for the tenant + caller. */
export interface MeetingProvider {
  provider: string; // 'microsoft' | 'google'
  label: string; // 'Microsoft Teams' | 'Google Meet'
  capabilities: string[];
  org_links: boolean;
  connected: boolean;
  can_create_link: boolean;
}
export interface MeetingProviders {
  enabled: boolean;
  default: string | null;
  providers: MeetingProvider[];
  count: number;
}

/** A page of messages. before_seq = history scroll (newest-first); after_seq =
 *  forward catch-up (oldest-first). */
export interface MessagePage {
  messages: Message[];
  next_before_seq?: number;
  next_after_seq?: number;
}

export interface ReactionResult {
  on: boolean;
  reaction_key: string;
  reactions: Record<string, number>;
  my_reactions: string[];
}

/** The approved reaction vocabulary (backend-enforced). The UI maps each key to a
 *  glyph so keys stay URL-safe names. */
export const REACTION_KEYS = ['thumbsup', 'heart', 'celebrate', 'laugh', 'sad', 'eyes'] as const;
export type ReactionKey = (typeof REACTION_KEYS)[number];
export const REACTION_GLYPH: Record<ReactionKey, string> = {
  thumbsup: '👍',
  heart: '❤️',
  celebrate: '🎉',
  laugh: '😄',
  sad: '😢',
  eyes: '👀',
};

export type SpaceKind = 'DEPARTMENT' | 'TEAM' | 'COMPANY';

/** Ask-Dana result: the posted question + Dana's reply, plus any confirm-first
 *  proposals (never auto-executed — the UI renders a confirm card). */
export interface DanaResult {
  user_message: Message;
  dana_message: Message;
  proposals?: unknown[];
}

/** Dana's fixed bot participant id (so the UI can label its messages as "Dana"). */
export const DANA_BOT_USER_ID = '00000000-0000-0000-0000-0000da4a0001';

/** SSE frame payload for the `message.created` event on the live stream. */
export interface StreamMessageEvent {
  message: Message;
  cursor: string; // the frame's id: — a resume token
}

// ── Availability (Slack-style status, KaziPeople way: derived first, manual second) ──
// The server decides precedence — approved leave beats anything a person set, then
// their unexpired manual status, then plain online/offline. The UI only renders what
// comes back; it never re-derives the winner.

export type AvailabilityKind =
  | 'AVAILABLE'
  | 'ON_LEAVE'
  | 'SICK'
  | 'IN_MEETING'
  | 'COMMUTING'
  | 'REMOTE'
  | 'FOCUS'
  | 'OFFLINE';

/** Where a status came from: an approved leave record, the person themselves, or
 *  simply whether their live session is up. */
export type AvailabilitySource = 'leave' | 'manual' | 'session';

export interface Availability {
  user_id: string;
  /** Server-owned; kept open (string) so a new kind never breaks the UI. */
  kind: AvailabilityKind | string;
  label: string;
  emoji?: string;
  /** The person's own note. */
  text?: string;
  until?: string;
  source: AvailabilitySource;
  /** Pre-formatted secondary line, e.g. "Until 10:45 AM". */
  detail?: string;
  online: boolean;
}

/** One pickable status in the "Set your status" dialog (the server owns the list). */
export interface AvailabilityOption {
  kind: string;
  label: string;
  emoji: string;
  /** 0 = until the end of today. */
  default_minutes: number;
  /** True when picking this reads like booking leave — the dialog says it doesn't. */
  leave_hint?: boolean;
}

/** GET /availability/me — my status, what I may pick, and the server's own note. */
export interface MyAvailability {
  availability: Availability;
  options: AvailabilityOption[];
  note: string;
}

/** PUT /availability/me body. */
export interface AvailabilityInput {
  kind: string;
  text?: string;
  minutes?: number;
  until?: string;
}
